import React from 'react';

import { formatSignedCurrency } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';

import {
  type ActivityPayment,
  type ActivitySplit,
  calculateUserPosition,
} from '@/utils/activityUtils';

interface UserPositionIndicatorProps {
  userId?: string;
  paidBy?: ActivityPayment[];
  splits?: ActivitySplit[];
  userShare?: number;
  currency: string;
  showBackground?: boolean;
  /** Compact variant: the words over the signed amount, right-aligned */
  variant?: 'default' | 'compact';
}

export function UserPositionIndicator({
  userId,
  paidBy,
  splits,
  userShare,
  currency,
  showBackground = false,
  variant = 'default',
}: UserPositionIndicatorProps) {
  const { t } = useTranslation();

  // Load expenses namespace for position labels
  useNamespace('expenses');

  const position = calculateUserPosition(
    userId,
    paidBy,
    splits,
    userShare,
    currency
  );

  if (position.type === 'none') {
    return null;
  }

  const isOwe = position.type === 'owe';
  const translationKey = isOwe
    ? 'expenses:labels.youOwe'
    : 'expenses:labels.youGetBack';
  const colorClass = isOwe
    ? 'text-red-600 dark:text-red-400'
    : 'text-green-600 dark:text-green-400';
  const signedAmount = formatSignedCurrency(
    isOwe ? -position.amount : position.amount,
    currency
  );

  if (variant === 'compact') {
    return (
      <div
        className={`flex flex-col items-end text-right ${colorClass}`}
        data-testid="user-position-indicator-compact"
      >
        <span className="block text-xs" data-testid="position-label-compact">
          {t(translationKey)}
        </span>
        <span
          className="block text-xs font-semibold"
          data-testid="position-amount-compact"
        >
          {signedAmount}
        </span>
      </div>
    );
  }

  const backgroundClass = isOwe
    ? 'bg-red-50 dark:bg-red-950/30 border-red-100 dark:border-red-900/50'
    : 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800';

  if (showBackground) {
    return (
      <div
        className={`flex items-center gap-2 px-4 py-3 rounded-lg border ${backgroundClass}`}
        data-testid="user-position-indicator-background"
      >
        <p
          className={`text-sm font-medium ${colorClass}`}
          data-testid={isOwe ? 'user-owes-amount' : 'user-gets-back-amount'}
        >
          {t(translationKey)}{' '}
          <span data-testid="position-amount">{signedAmount}</span>
        </p>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-2 ${colorClass}`}
      data-testid="user-position-indicator"
    >
      <p
        className="text-sm font-medium"
        data-testid={isOwe ? 'user-owes-simple' : 'user-gets-back-simple'}
      >
        {t(translationKey)}{' '}
        <span data-testid="position-amount-simple">{signedAmount}</span>
      </p>
    </div>
  );
}
